import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TanStackTableRoot } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useThemeDrawer } from '~/widgets/theme-widget/theme-drawer/useThemeDrawer';

import { LoaderCell } from './loader-cell/LoaderCell';
import { RankingMethodologySection } from './ranking-methodology-section/RankingMethodologySection';
import * as S from './ThemeExposureTable.styles';
import { getColumns, RANK_COLUMN_ID } from './utils/getColumns';

interface ThemeExposureTableProps {
  entity: Entity;
}

export const ThemeExposureTable = ({ entity }: ThemeExposureTableProps) => {
  const { t } = useTranslation(['widget']);
  const { openDrawer } = useThemeDrawer();
  const { trackThemeOverviewDrawerOpen } = usePosthogEvents();

  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isFetching, error } = useCompanyTheme({
    entityTag: entity.tag,
  });

  const columns = getColumns({ t });

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    throw error;
  }

  return (
    <S.TableWrapper ref={containerRef} $isLoading={isFetching}>
      <TanStackTableRoot data-testid="theme-exposure-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <S.StyledTableHeader
                    key={header.id}
                    alignment="left"
                    isHoverEnabled={false}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </S.StyledTableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isFetching ? (
            <LoaderCell numberOfRows={3} />
          ) : (
            table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <S.StyledTableCell
                      key={cell.id}
                      alignment="left"
                      isNumber={cell.column.id === RANK_COLUMN_ID}
                      onClick={() => {
                        trackThemeOverviewDrawerOpen(
                          POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW
                        );
                        openDrawer(row.original.theme.id);
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </S.StyledTableCell>
                  );
                })}
              </tr>
            ))
          )}
          <tr data-testid="footer-row">
            <S.FooterRow
              colspan={table.getHeaderGroups()[0].headers.length}
              isNumber={false}
            >
              <RankingMethodologySection rankingType={'companyToTheme'} />
            </S.FooterRow>
          </tr>
        </tbody>
      </TanStackTableRoot>
    </S.TableWrapper>
  );
};
